import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import bem from 'utils/bem';
import { Formik } from 'formik';
import Recaptcha from 'react-google-recaptcha';
import { feedbackValidationSchema } from './validationSchema';
import TextField from 'forms/fields/TextField';
import UserAgreementCheckboxField from 'forms/fields/UserAgreementCheckboxField';
import { IconButton } from 'components/Button';
import FormikPhoneField from 'forms/fields/PhoneField/FormikPhoneField';
import TextareaField from 'forms/fields/TextareaField';
import Model from '../../../../model';

import './index.sass';
import FeedbackFormSuccess from 'forms/forms/FeedbackForm/FeedbackFormSuccess';
import { observer } from 'mobx-react-lite';

const initialValues = {
    name: '',
    description: '',
    customerPhone: '',
    acceptConditions: false,
    recaptcha: false
};

const { block, el } = bem('feedback-form', 'form');

const FeedbackForm = ({ buttonMod = 'transparent-gold', userAgreementMod = 'short', useRecaptcha = process.env.NODE_ENV === 'production' }) => {

    const formNode = useRef();

    const [captchaLoaded, setCaptchaLoaded] = useState(false);

    const recaptchaInstance = useRef(null);

    const { sendFeedback, setSubmitSuccess, submitSuccess } = Model.feedbackForm;

    useEffect(() => {
        return () => {
            setSubmitSuccess(false);
        }
    }, []);

    const submitForm = async (values, bag) => {
        const { feedbackId } = await sendFeedback(values);
        if (feedbackId) {
            bag.resetForm();
            setSubmitSuccess(true)
        } else {
            throw 'feedbackId is null';
        }
    }

    const onloadCallback = () => {
        setCaptchaLoaded(true);
    };

    const executeCaptcha = () => {
        recaptchaInstance.current.execute();
    };

    const feedbackFormRef = useCallback((node) => {
        if (node !== null && useRecaptcha && !captchaLoaded) {
            formNode.current = node;
            const initializeRecaptcha = () => {
                const recaptchaScript = document.createElement('script');
                recaptchaScript.src = 'https://www.google.com/recaptcha/api.js?hl=ru&render=explicit';
                recaptchaScript.defer = true;
                document.body.appendChild(recaptchaScript);
            }
            if ('IntersectionObserver' in window) {
                const io = new IntersectionObserver(
                    entries => {
                        if (entries[0].isIntersecting) {
                            io.unobserve(node)
                            initializeRecaptcha()
                        }
                    }
                );
                io.observe(node);
            } else {
                initializeRecaptcha()
            }
        }
    }, [captchaLoaded]);

    if (submitSuccess && !useRecaptcha) {
        return <FeedbackFormSuccess />
    }

    const Form = memo(({ handleSubmit, handleBlur, isSubmitting, verifyCallback }) => {
        return <form ref={ feedbackFormRef } onSubmit={ handleSubmit }>
            <>
                <div className={el('fields')}>
                    <TextField className={ el('name') } name='name' placeholder='Ваше имя' onBlur={handleBlur} />
                    <FormikPhoneField className={ el('phone') } name={ 'customerPhone' } placeholder={ 'Номер телефона' } onBlur={handleBlur} />
                    <TextareaField className={ el('description') } name='description' placeholder='Сообщение' onBlur={handleBlur}/>
                </div>
                <div className={el('submit-block')}>
                    <UserAgreementCheckboxField name='acceptConditions' mod={ userAgreementMod } />
                    <IconButton className={ el('button', 'submit') }
                                name={'arrow-right'}
                                type='submit'
                                mod={ buttonMod }
                                disabled={ isSubmitting }>
                        Отправить
                    </IconButton>
                </div>
                { useRecaptcha && <Recaptcha
                    ref={ recaptchaInstance }
                    sitekey='6Lf8WQsmAAAAAFzvkHLzWbIXD9M_ysvNDPJzNFh8'
                    render='explicit'
                    hl='ru'
                    asyncScriptOnLoad={ onloadCallback }
                    onChange={ verifyCallback }
                    size={ 'invisible' }
                /> }
            </>
        </form>
    });

    return <div className={ block() }>
        <Formik
            initialValues={ initialValues }
            validateOnChange={ false }
            validateOnBlur={ false }
            validationSchema={ feedbackValidationSchema(useRecaptcha) }
            onSubmit={ submitForm }
            enableReinitialize={ true }
        >
            { (form) => {
                const verifyCaptcha = useCallback((response) => {
                    form.setFieldValue('recaptcha', response);
                    form.setSubmitting(false);
                }, []);
                const handleBlur = useCallback((e) => {
                    if (!form.values.recaptcha && useRecaptcha) {
                        executeCaptcha();
                        form.setSubmitting(true);
                    }
                    form.handleBlur(e);
                }, [form.values.recaptcha])
                return <Form verifyCallback={verifyCaptcha} isSubmitting={form.isSubmitting} handleSubmit={form.handleSubmit} handleBlur={handleBlur}/>
            }}
        </Formik>
    </div>
};

export default observer(FeedbackForm);
